import "./modal.scss";
import React, { FC, MouseEvent, useEffect } from "react";
import GreaterIcon from "../../assets/icons/svg/next.svg?react";
import ReactDOM from "react-dom";
import { connect, ConnectedProps } from "react-redux";
import { updateModal } from "@/store/app/app.slice";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
  height?: string;
  bgColor?: string;
  closeOnOutsideClick?: boolean;
  otherPropertiesStyle?: any;
  applyOverlay?: boolean;
  hideCloseButton?: boolean;
  closeOnEsc?: boolean;
  headerRight?: React.ReactNode;
  zIndex?: number;
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  width = "80vw",
  height = "auto",
  footer,
  bgColor,
  updateModal,
  closeOnOutsideClick = false,
  otherPropertiesStyle,
  applyOverlay = true,
  hideCloseButton = false,
  closeOnEsc = false,
  headerRight,
  zIndex,
}: Props) => {
  useEffect(() => {
    updateModal(isOpen);
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        updateModal(false);
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, closeOnEsc, onClose, updateModal]);

  if (!isOpen) return null;

  const handleOutsideClick = (e: MouseEvent<HTMLDivElement>) => {
    if (
      closeOnOutsideClick &&
      e.target instanceof HTMLDivElement &&
      e.target.classList.contains("modal")
    ) {
      onClose();
      updateModal(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="modal d-block overflow-hidden"
      style={{
        backgroundColor: applyOverlay ? "rgba(0, 0, 0, 0.7)" : "transparent",
        backdropFilter: applyOverlay ? "blur(2px)" : "none",
        WebkitBackdropFilter: applyOverlay ? "blur(2px)" : "none",
        ...(zIndex != null ? { zIndex } : {}),
      }}
      onClick={handleOutsideClick}
    >
      <div
        className="modal-dialog modal-dialog-centered m-0 h-100 top-50 translate-middle"
        style={{ width, left: "50%", maxWidth: "90%", ...otherPropertiesStyle }}
      >
        <div
          className="modal-content"
          style={{
            height,
            maxHeight: "100%",
            width,
            ...(otherPropertiesStyle?.minHeight && {
              minHeight: otherPropertiesStyle.minHeight,
            }),
          }}
        >
          {title && (
            <div
              className={`modal-header ${bgColor} ${bgColor ? "border-0" : ""}`}
            >
              <h5 className="modal-title text-uppercase">
                {title?.split(">")[0]?.trim()}
                {title?.split(">")[1]?.trim() && (
                  <GreaterIcon className="iconSvg mx-2" />
                )}
                <span className="text-secondary fw-normal">
                  {title?.split(">")[1]?.trim()}
                </span>
              </h5>
              <div className="d-flex align-items-center gap-2 ms-auto">
                {headerRight}
                {!hideCloseButton && (
                  <button
                    type="button"
                    className="btn-close pointer"
                    onClick={() => {
                      updateModal(false);

                      onClose();
                    }}
                    aria-label="Close"
                    style={{ marginRight: "6px" }}
                  ></button>
                )}
              </div>
            </div>
          )}
          <div
            className={`modal-body overflow-x-hidden ${bgColor} ${
              !footer ? "rounded" : ""
            }`}
            style={{
              paddingTop: bgColor ? 0 : "",
              height: "calc(100% - 110px)",
              overflowY: "auto",
            }}
          >
            {children}
          </div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body
  );
};

const mapDispatchToProps = {
  updateModal,
};
const connector = connect(null, mapDispatchToProps);
type Props = ConnectedProps<typeof connector> & ModalProps;

export default connector(Modal);
